import React, { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './Awards.css';
import { getLangFromPath, t, tObject } from '../utils/i18n';

type AwardItem = {
  title: string;
  imageAlt: string;
  paragraphs: string[];
};

type AwardSection = {
  headline: string;
  paragraphs: string[];
};

const AWARD_2026_IMAGES = [
  '/images/awards/2026/world-gin-awards.jpg',
  '/images/awards/2026/gin-guide-awards.jpg',
  '/images/awards/2026/tokyo-whisky-spirits.jpg',
];

const Awards: React.FC = () => {
  const location = useLocation();
  const lang = getLangFromPath(location.pathname, 'en');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const year2026Items = (tObject(lang, 'awards.year2026.items') ||
    tObject('en', 'awards.year2026.items') ||
    []) as AwardItem[];
  const year2025Section = (tObject(lang, 'awards.year2025.section') ||
    tObject('en', 'awards.year2025.section') ||
    { headline: '', paragraphs: [] }) as AwardSection;
  const year2026Label = t(lang, 'awards.year2026.label');
  const year2025Label = t(lang, 'awards.year2025.label');

  return (
    <div className="awards-page">
      <div className="awards-container">
        <div className="awards-icon" aria-hidden="true">
          <i className="fas fa-trophy"></i>
        </div>

        <section className="awards-year-block" aria-labelledby="awards-year-2026">
          <h2 id="awards-year-2026" className="awards-year-heading">
            {year2026Label}
          </h2>
          <div className="awards-2026-list">
            {year2026Items.map((item, idx) => (
              <article key={item.title} className="awards-2026-card">
                <div className="awards-2026-image-wrap">
                  <img
                    src={AWARD_2026_IMAGES[idx]}
                    alt={item.imageAlt}
                    loading="lazy"
                    decoding="async"
                    className="awards-2026-image"
                  />
                </div>
                <div className="awards-2026-content">
                  <h3 className="awards-2026-title">{item.title}</h3>
                  <div className="awards-body">
                    {item.paragraphs.map((p, pIdx) => (
                      <p key={pIdx}>{p}</p>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="awards-year-block awards-year-block--2025" aria-labelledby="awards-year-2025">
          <h2 id="awards-year-2025" className="awards-year-heading">
            {year2025Label}
          </h2>
          <h3 className="awards-headline">{year2025Section.headline}</h3>
          <div className="awards-body">
            {year2025Section.paragraphs.map((p, idx) => (
              <p key={idx} className={p.includes('“') || p.includes('"') ? 'awards-quote' : ''}>
                {p}
              </p>
            ))}
          </div>
          <div className="awards-badges">
            <img src="/images/awards/badge-1.png" alt="Gin Guide Award badge 1" loading="lazy" />
            <img src="/images/awards/badge-2.png" alt="Gin Guide Award badge 2" loading="lazy" />
            <img src="/images/awards/badge-3.png" alt="Gin Guide Award badge 3" loading="lazy" />
          </div>
          <div className="awards-media">
            <div className="awards-photos">
              <img src="/images/awards/1.jpg" alt="Damascus Gin awards 1" loading="lazy" decoding="async" />
              <img src="/images/awards/2.jpg" alt="Damascus Gin awards 2" loading="lazy" decoding="async" />
            </div>
            <div className={`awards-video ${isPlaying ? 'playing' : ''}`}>
              <video
                ref={videoRef}
                controls={isPlaying}
                preload="metadata"
                poster="/images/awards/1.jpg"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
              >
                <source src="/images/awards/3.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <button
                type="button"
                aria-label="Play video"
                className="play-overlay"
                onClick={() => {
                  const el = videoRef.current;
                  if (!el) return;
                  el.play();
                }}
              >
                ▶
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Awards;
